// System dots with own markers, shield rings, G-66 alliance rings and type icons (spec 15c §3.3).
// Each system is a box drawn in screen pixels at the largest dot size and scaled with the camera,
// so dots stay between DOT_RADIUS_PX.min and .max on screen at every zoom. Type icons give way to
// labels (15c feedback, `declutter.ts`).
import type { MapSystem } from '@stars-and-ships/api-types';
import { Container, Graphics } from 'pixi.js';
import { MAP_COLORS } from '../../theme';
import { type Candidate, iconKey, type Obstacle, type Offset, PRIORITY } from '../declutter';
import { HOME_MARKER, placeGlyph, TYPE_ICONS } from '../glyphs';
import { allianceRingColor, ownMarker, systemColor } from '../markers';
import { DOT_RADIUS_PX, dotRadiusPx, type ZoomTier } from '../zoomTier';

/** Sizes in pixels at the largest dot (DOT_RADIUS_PX.max). */
const R = DOT_RADIUS_PX.max;
const HOME_RADIUS = R * 1.25;
const COLONY_RING = R * 1.5;
const SHIELD_RING = R * 1.9;
const ALLIANCE_RING = R * 2.35;
const HOME_OUTLINE = R * 1.6;
const TYPE_ICON = { dx: R * 1.7, dy: -R * 1.7, size: R * 0.6 };
export const SELECTION_RING = R * 2.8;

/** Scale of a box drawn at the largest dot size, inside the world container. */
export const boxScale = (cameraScale: number) => dotRadiusPx(cameraScale) / R / cameraScale;

export interface SystemsLayer {
  container: Container;
  setView(tier: ZoomTier, scale: number): void;
  /** Screen rectangles of the type icons the tier shows. */
  candidates(): Candidate[];
  /** Every system's dot with its outermost ring: nothing of another system covers it. */
  obstacles(): Obstacle[];
  applyShown(shown: ReadonlyMap<string, Offset>): void;
}

export function systemsLayer(input: {
  systems: readonly MapSystem[];
  playerId: string | undefined;
  allianceId: string | null | undefined;
  homeSystemId: string | null;
  ownSystemIds: ReadonlySet<string>;
}): SystemsLayer {
  const container = new Container();
  const boxes: Container[] = [];
  const typeIcons: { system: MapSystem; icon: Graphics }[] = [];
  /** Half the width of each system's marker, at the largest dot size. */
  const extents: { system: MapSystem; half: number }[] = [];
  for (const s of input.systems) {
    const g = new Graphics();
    const ring = allianceRingColor(s, input.playerId, input.allianceId);
    const marker = ownMarker(s.id, input.homeSystemId, input.ownSystemIds);
    extents.push({
      system: s,
      half: Math.max(
        ring !== null ? ALLIANCE_RING : 0,
        s.ownerShielded ? SHIELD_RING : 0,
        marker === 'home' ? HOME_OUTLINE * 1.2 : marker === 'colony' ? COLONY_RING : R
      ),
    });
    if (ring !== null) {
      g.circle(0, 0, ALLIANCE_RING).stroke({ width: 1, color: ring, alpha: 0.85, pixelLine: true });
    }
    if (s.ownerShielded) {
      g.circle(0, 0, SHIELD_RING).stroke({
        width: 1,
        color: MAP_COLORS.shield,
        alpha: 0.7,
        pixelLine: true,
      });
    }
    if (marker === 'home') {
      g.poly(placeGlyph(HOME_MARKER, 0, 0, HOME_OUTLINE)).stroke({
        width: 1,
        color: MAP_COLORS.own,
        pixelLine: true,
      });
    } else if (marker === 'colony') {
      g.circle(0, 0, COLONY_RING).stroke({ width: 1, color: MAP_COLORS.own, pixelLine: true });
    }
    g.circle(0, 0, marker === 'home' ? HOME_RADIUS : R).fill(
      systemColor(s, input.playerId, input.allianceId)
    );
    const box = new Container();
    box.position.set(s.x, s.y);
    box.addChild(g);
    if (s.systemType !== 'standard') {
      const icon = TYPE_ICONS[s.systemType];
      const points = placeGlyph(icon.points, TYPE_ICON.dx, TYPE_ICON.dy, TYPE_ICON.size);
      const t = new Graphics();
      if (icon.filled) t.poly(points).fill(MAP_COLORS.label);
      else t.poly(points).stroke({ width: 1, color: MAP_COLORS.label, pixelLine: true });
      box.addChild(t);
      typeIcons.push({ system: s, icon: t });
    }
    container.addChild(box);
    boxes.push(box);
  }
  let iconsInTier = false;
  let viewScale = 1;
  return {
    container,
    setView(tier, scale) {
      viewScale = scale;
      const k = boxScale(scale);
      for (const box of boxes) box.scale.set(k);
      iconsInTier = tier !== 'far';
      for (const { icon } of typeIcons) icon.visible = iconsInTier;
    },
    candidates() {
      if (!iconsInTier) return [];
      const px = dotRadiusPx(viewScale) / R;
      const size = TYPE_ICON.size * px;
      return typeIcons.map(({ system }) => ({
        key: iconKey(system.id),
        systemId: system.id,
        priority: PRIORITY.icon,
        rects: [
          {
            x: system.x * viewScale + TYPE_ICON.dx * px - size,
            y: system.y * viewScale + TYPE_ICON.dy * px - size,
            w: size * 2,
            h: size * 2,
          },
        ],
      }));
    },
    obstacles() {
      const px = dotRadiusPx(viewScale) / R;
      return extents.map(({ system, half }) => ({
        systemId: system.id,
        rect: {
          x: system.x * viewScale - half * px,
          y: system.y * viewScale - half * px,
          w: half * 2 * px,
          h: half * 2 * px,
        },
      }));
    },
    applyShown(keys) {
      for (const { system, icon } of typeIcons) {
        icon.visible = iconsInTier && keys.has(iconKey(system.id));
      }
    },
  };
}
